import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import "@/App.css";
import SEO from "../components/shared/SEO";
import { 
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Zap,
  Target,
  Users,
  CheckCircle2,
  ChevronRight,
  Lightbulb,
  Settings,
  BarChart3,
  RefreshCw,
  Award,
  AlertTriangle,
  Calendar,
  ChevronUp,
  Search,
  FileText,
  Mail,
  MessageSquare,
  UserCheck,
  Workflow,
  Download,
  Play,
  ArrowUpRight,
  X,
  Check
} from "lucide-react";

// Brand Colors
const BRAND = {
  gold: "#D4AF37",
  goldLight: "#E8C547",
  charcoal: "#1C1C1C",
  surface: "#0A0A0A",
  text: "#FAFAFA"
};

// Workflow Data - 6 Core AI SDR Workflows
const WORKFLOWS = [
  {
    id: "prospecting",
    number: 1,
    title: "Prospecting & Intent Agent",
    shortTitle: "Prospecting",
    icon: Search,
    color: "blue",
    description: "Identify not just who should be a customer, but who is most likely to become one right now.",
    inputs: [
      "Defined ICP and target segments",
      "Intent data sources connected (G2, Bombora, TrustRadius)",
      "Trigger events configured (funding, hiring, tech changes)",
      "First-party signals (website visits, content engagement)"
    ],
    systemActions: [
      "Prioritizes accounts by intent score",
      "Recommends contacts within target accounts",
      "Scores timing based on signal recency",
      "Flags accounts showing active research behavior"
    ],
    outputs: [
      "Ranked account list with intent scores",
      "SDR target list with contact priorities",
      "Timing recommendations per account"
    ],
    kpis: [
      "Conversion rate by intent tier",
      "Pipeline generated per account segment",
      "Time-to-contact for high-intent accounts"
    ],
    failures: [
      "Static list building without signal weighting",
      "No prioritization logic—treating all accounts equally",
      "Weak or outdated data sources"
    ],
    proTip: "Top teams don't build lead lists. They build prioritization engines."
  },
  {
    id: "research",
    number: 2,
    title: "Account Research Agent",
    shortTitle: "Research",
    icon: Target,
    color: "purple",
    description: "Understand how decisions actually get made inside target accounts and map the buying committee.",
    inputs: [
      "Target account list from prospecting",
      "Org chart and role data",
      "LinkedIn profiles of key contacts",
      "Company news, press releases, earnings calls"
    ],
    systemActions: [
      "Maps buying committee by role and influence",
      "Identifies decision-makers vs. influencers vs. users",
      "Surfaces relevant pain points per persona",
      "Tracks recent company events and initiatives"
    ],
    outputs: [
      "Buying committee map with roles defined",
      "Persona-specific pain points and priorities",
      "Recent triggers and conversation hooks"
    ],
    kpis: [
      "Multi-threading rate per account",
      "Contact-to-meeting conversion by role",
      "Account penetration depth"
    ],
    failures: [
      "Single-threaded outreach to one contact",
      "Same message to all personas regardless of role",
      "Missing key decision-makers in the committee"
    ],
    proTip: "If you send the same message to the CFO, Controller, and AR Manager, you've lost account research credibility."
  },
  {
    id: "content",
    number: 3,
    title: "Content & Messaging Playbook Agent",
    shortTitle: "Content",
    icon: FileText,
    color: "orange",
    description: "Build a living, continuously updated knowledge layer that everything else pulls from.",
    inputs: [
      "Internal sales playbooks and battle cards",
      "Live call recordings (wins and losses)",
      "CEO/founder value proposition framing",
      "Marketing collateral, case studies, testimonials"
    ],
    systemActions: [
      "Organizes content by persona and use case",
      "Tracks product updates and positioning changes",
      "Captures tribal knowledge from top performers",
      "Updates playbook as company evolves"
    ],
    outputs: [
      "Living playbook indexed by persona/stage",
      "Proof points and social proof by segment",
      "Competitive positioning and objection responses"
    ],
    kpis: [
      "Message relevance scores",
      "Reply rates by content type",
      "Playbook adoption across team"
    ],
    failures: [
      "Generic messaging that sounds interchangeable",
      "Outdated proof points and case studies",
      "No input from actual customer conversations"
    ],
    proTip: "Without a living playbook, your system generates content from generic training data. With it, your system speaks in your voice."
  },
  {
    id: "outreach",
    number: 4,
    title: "Outreach Execution Agent",
    shortTitle: "Outreach",
    icon: Mail,
    color: "green",
    description: "Combine everything upstream into communication that feels relevant, timely, and credible.",
    inputs: [
      "Account research and buying committee data",
      "Persona-specific pain points",
      "Content playbook and proof points",
      "Optimal timing data from engagement patterns"
    ],
    systemActions: [
      "Drafts personalized email and LinkedIn messages",
      "Matches CTA to buyer stage",
      "Schedules sends based on engagement data",
      "A/B tests subject lines and openers"
    ],
    outputs: [
      "Personalized outreach drafts for review",
      "Send schedule optimized by persona",
      "Performance data per message variant"
    ],
    kpis: [
      "Open rates and reply rates",
      "Meeting conversion rate",
      "Message-to-meeting velocity"
    ],
    failures: [
      "Purely transactional CTAs in every message",
      "Generic personalization tokens only",
      "Ignoring timing and engagement patterns"
    ],
    proTip: "Not every message should push for a meeting. Some should build context, share insight, or simply establish relevance."
  },
  {
    id: "followup",
    number: 5,
    title: "Follow-Up & Engagement Agent",
    shortTitle: "Follow-Up",
    icon: MessageSquare,
    color: "cyan",
    description: "Manage sequencing, timing, and response handling while knowing when to bring a human into the conversation.",
    inputs: [
      "Prior interaction history",
      "Response classification (positive, neutral, objection)",
      "Real objection data from CRM and calls",
      "Human-in-loop escalation rules"
    ],
    systemActions: [
      "Triggers follow-ups at optimal intervals",
      "Classifies responses and suggests reply paths",
      "Surfaces context for human handoff",
      "Handles objections with trained responses"
    ],
    outputs: [
      "Sequenced follow-up queue",
      "Human escalation alerts with context",
      "Objection handling recommendations"
    ],
    kpis: [
      "Follow-up to reply conversion",
      "Time-to-human-handoff",
      "Objection resolution rate"
    ],
    failures: [
      "Robotic follow-ups that ignore prior context",
      "No human handoff for complex responses",
      "Generic objection handling scripts"
    ],
    proTip: "The moment a prospect replies, context matters. Train the agent on real objection data from your buyers, not generic scripts."
  },
  {
    id: "connected",
    number: 6,
    title: "Connected System & Feedback Loop",
    shortTitle: "System",
    icon: RefreshCw,
    color: "gold",
    description: "Design how information flows between each workflow to create compounding improvement over time.",
    inputs: [
      "Performance data from all workflows",
      "Win/loss analysis from closed deals",
      "SDR feedback and observations",
      "Market and competitive intelligence"
    ],
    systemActions: [
      "Reinforces high-performing signals",
      "Deprioritizes low-converting segments",
      "Updates targeting based on outcomes",
      "Maintains consistency across agents"
    ],
    outputs: [
      "Optimized targeting criteria",
      "Updated scoring models",
      "System health dashboard"
    ],
    kpis: [
      "System conversion rate over time",
      "Time-to-optimization cycle",
      "Cross-workflow consistency score"
    ],
    failures: [
      "Siloed workflows that don't share data",
      "No feedback loop from outcomes to targeting",
      "Inconsistent tone and messaging across agents"
    ],
    proTip: "This is the difference between having AI tools and having an AI-powered sales development engine."
  }
];

// Implementation Phases
const PHASES = [
  {
    phase: 1,
    title: "Foundation",
    description: "Define ICP, connect data sources, establish baseline metrics",
    duration: "Weeks 1-2",
    tasks: ["ICP definition", "Data source audit", "Baseline KPIs"]
  },
  {
    phase: 2,
    title: "Workflow Build",
    description: "Build and train each workflow individually before connecting",
    duration: "Weeks 3-6",
    tasks: ["Build workflows 1-6", "Individual testing", "Quality gates"]
  },
  {
    phase: 3,
    title: "Execution",
    description: "Connect workflows, enable data flow, launch with monitoring",
    duration: "Weeks 7-8",
    tasks: ["System integration", "Pilot launch", "Performance monitoring"]
  },
  {
    phase: 4,
    title: "Optimization",
    description: "Analyze results, refine models, scale what works",
    duration: "Ongoing",
    tasks: ["Weekly reviews", "Model updates", "Scale high performers"]
  }
];

// Key Takeaways
const TAKEAWAYS = [
  "AI SDRs fail when built as a single monolithic agent",
  "Systems of trained workflows outperform standalone tools",
  "Training matters more than prompting",
  "Execution discipline drives performance, not technology alone"
];

// Header Component
const Header = () => {
  const navigate = useNavigate();
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <svg width="24" height="24" viewBox="0 0 64 64" fill="none">
              <rect x="6" y="46" width="11" height="14" fill={BRAND.gold} opacity="0.5" rx="2"/>
              <rect x="20" y="36" width="11" height="24" fill={BRAND.gold} opacity="0.65" rx="2"/>
              <rect x="34" y="24" width="11" height="36" fill={BRAND.gold} opacity="0.8" rx="2"/>
              <rect x="48" y="10" width="11" height="50" fill={BRAND.gold} rx="2"/>
            </svg>
            <span className="text-sm font-bold text-white">LRS</span>
          </button>
          
          <button 
            onClick={() => navigate('/resources')}
            className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Resources</span>
          </button>
        </div>
      </div>
    </header>
  );
};

// Interactive System Diagram Component
const SystemDiagram = ({ activeWorkflow, setActiveWorkflow, scrollToWorkflow }) => {
  const colorMap = {
    blue: { bg: "bg-blue-500/20", border: "border-blue-500/40", text: "text-blue-400", glow: "shadow-blue-500/20" },
    purple: { bg: "bg-purple-500/20", border: "border-purple-500/40", text: "text-purple-400", glow: "shadow-purple-500/20" },
    orange: { bg: "bg-orange-500/20", border: "border-orange-500/40", text: "text-orange-400", glow: "shadow-orange-500/20" },
    green: { bg: "bg-green-500/20", border: "border-green-500/40", text: "text-green-400", glow: "shadow-green-500/20" },
    cyan: { bg: "bg-cyan-500/20", border: "border-cyan-500/40", text: "text-cyan-400", glow: "shadow-cyan-500/20" },
    gold: { bg: "bg-[#D4AF37]/20", border: "border-[#D4AF37]/40", text: "text-[#D4AF37]", glow: "shadow-[#D4AF37]/20" }
  };

  return (
    <div className="relative">
      {/* Desktop Layout - 2x3 Grid with Connecting Lines */}
      <div className="hidden lg:block">
        <div className="grid grid-cols-3 gap-4 relative">
          {WORKFLOWS.map((workflow, index) => {
            const colors = colorMap[workflow.color];
            const isActive = activeWorkflow === workflow.id;
            const Icon = workflow.icon;
            
            return (
              <button
                key={workflow.id}
                onClick={() => {
                  setActiveWorkflow(workflow.id);
                  scrollToWorkflow(workflow.id);
                }}
                onMouseEnter={() => setActiveWorkflow(workflow.id)}
                onMouseLeave={() => setActiveWorkflow(null)}
                className={`relative p-4 rounded-xl border transition-all duration-300 text-left group ${
                  isActive 
                    ? `${colors.bg} ${colors.border} shadow-lg ${colors.glow}` 
                    : 'bg-[#1C1C1C] border-white/10 hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isActive ? colors.bg : 'bg-white/5'
                  }`}>
                    <Icon className={`w-5 h-5 ${isActive ? colors.text : 'text-zinc-400'}`} />
                  </div>
                  <span className={`text-xs font-bold ${isActive ? colors.text : 'text-zinc-500'}`}>
                    {workflow.number}
                  </span>
                </div>
                <h4 className={`font-semibold text-sm mb-1 ${isActive ? 'text-white' : 'text-zinc-300'}`}>
                  {workflow.shortTitle}
                </h4>
                <p className="text-xs text-zinc-500 line-clamp-2">
                  {workflow.description.slice(0, 60)}...
                </p>
                
                {/* Arrow indicator */}
                <ArrowUpRight className={`absolute top-3 right-3 w-4 h-4 transition-all ${
                  isActive ? `${colors.text} opacity-100` : 'text-zinc-600 opacity-0 group-hover:opacity-100'
                }`} />
              </button>
            );
          })}
        </div>
        
        {/* Connecting arrows visual */}
        <div className="mt-4 flex justify-center items-center gap-2 text-zinc-500">
          <span className="text-xs">Flow:</span>
          {WORKFLOWS.map((w, i) => (
            <div key={w.id} className="flex items-center">
              <span className={`text-xs font-medium ${colorMap[w.color].text}`}>{w.number}</span>
              {i < WORKFLOWS.length - 1 && <ChevronRight className="w-3 h-3 mx-1" />}
            </div>
          ))}
          <RefreshCw className="w-3 h-3 ml-1 text-[#D4AF37]" />
        </div>
      </div>

      {/* Mobile Layout - 3x2 Grid */}
      <div className="lg:hidden">
        <div className="grid grid-cols-3 gap-2 pb-4">
          {WORKFLOWS.map((workflow) => {
            const colors = colorMap[workflow.color];
            const isActive = activeWorkflow === workflow.id;
            const Icon = workflow.icon;
            
            return (
              <button
                key={workflow.id}
                onClick={() => {
                  setActiveWorkflow(workflow.id);
                  scrollToWorkflow(workflow.id);
                }}
                className={`p-2.5 rounded-lg border transition-all text-center ${
                  isActive 
                    ? `${colors.bg} ${colors.border}` 
                    : 'bg-[#1C1C1C] border-white/10'
                }`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center mx-auto mb-1.5 ${colors.bg}`}>
                  <Icon className={`w-3.5 h-3.5 ${colors.text}`} />
                </div>
                <div className={`text-[9px] font-bold mb-0.5 ${colors.text}`}>{workflow.number}</div>
                <div className="text-[10px] font-medium text-white leading-tight">{workflow.shortTitle}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Workflow Section Component
const WorkflowSection = ({ workflow }) => {
  const colorMap = {
    blue: { accent: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30" },
    purple: { accent: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/30" },
    orange: { accent: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/30" },
    green: { accent: "text-green-400", bg: "bg-green-500/10", border: "border-green-500/30" },
    cyan: { accent: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/30" },
    gold: { accent: "text-[#D4AF37]", bg: "bg-[#D4AF37]/10", border: "border-[#D4AF37]/30" }
  };
  
  const colors = colorMap[workflow.color];
  const Icon = workflow.icon;

  return (
    <section id={`workflow-${workflow.id}`} className="scroll-mt-32">
      {/* Workflow Header */}
      <div className="flex items-start gap-4 mb-6">
        <div className={`w-14 h-14 rounded-xl ${colors.bg} ${colors.border} border flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-7 h-7 ${colors.accent}`} />
        </div>
        <div>
          <div className={`text-xs font-bold ${colors.accent} mb-1`}>Workflow {workflow.number}</div>
          <h3 className="text-xl sm:text-2xl font-bold text-white">{workflow.title}</h3>
          <p className="text-zinc-400 mt-1">{workflow.description}</p>
        </div>
      </div>

      {/* Operator Checklist */}
      <div className="bg-[#1C1C1C] rounded-2xl border border-white/5 p-6 mb-4">
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
          Operator Checklist
        </h4>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Inputs */}
          <div>
            <h5 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-400" />
              Inputs
            </h5>
            <ul className="space-y-2">
              {workflow.inputs.map((input) => (
                <li key={`input-${input.slice(0, 20)}`} className="flex items-start gap-2 text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                  {input}
                </li>
              ))}
            </ul>
          </div>

          {/* System Actions */}
          <div>
            <h5 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-purple-400" />
              System Actions
            </h5>
            <ul className="space-y-2">
              {workflow.systemActions.map((action) => (
                <li key={`action-${action.slice(0, 20)}`} className="flex items-start gap-2 text-sm text-zinc-400">
                  <Settings className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                  {action}
                </li>
              ))}
            </ul>
          </div>

          {/* Outputs */}
          <div>
            <h5 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400" />
              Outputs
            </h5>
            <ul className="space-y-2">
              {workflow.outputs.map((output) => (
                <li key={`output-${output.slice(0, 20)}`} className="flex items-start gap-2 text-sm text-zinc-400">
                  <ArrowRight className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                  {output}
                </li>
              ))}
            </ul>
          </div>

          {/* KPIs */}
          <div>
            <h5 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
              KPIs
            </h5>
            <ul className="space-y-2">
              {workflow.kpis.map((kpi) => (
                <li key={`kpi-${kpi.slice(0, 20)}`} className="flex items-start gap-2 text-sm text-zinc-400">
                  <BarChart3 className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                  {kpi}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Common Failure Modes */}
      <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5 mb-4">
        <h4 className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Common Failure Modes
        </h4>
        <ul className="space-y-2">
          {workflow.failures.map((failure) => (
            <li key={`failure-${failure.slice(0, 20)}`} className="flex items-start gap-2 text-sm text-zinc-400">
              <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              {failure}
            </li>
          ))}
        </ul>
      </div>

      {/* Pro Tip */}
      <div className={`${colors.bg} ${colors.border} border rounded-xl p-5`}>
        <div className="flex items-start gap-3">
          <Lightbulb className={`w-5 h-5 ${colors.accent} flex-shrink-0 mt-0.5`} />
          <div>
            <h5 className={`text-sm font-semibold ${colors.accent} mb-1`}>Pro Tip</h5>
            <p className="text-sm text-zinc-300">{workflow.proTip}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Implementation Roadmap Component
const ImplementationRoadmap = () => (
  <section id="roadmap" className="py-16 scroll-mt-20">
    <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">
      Implementation Roadmap
    </h2>
    
    {/* Desktop - Horizontal */}
    <div className="hidden md:flex items-start justify-between relative">
      {/* Connecting line */}
      <div className="absolute top-8 left-[10%] right-[10%] h-px bg-gradient-to-r from-[#D4AF37]/50 via-[#D4AF37] to-[#D4AF37]/50" />
      
      {PHASES.map((phase, i) => (
        <div key={phase.phase} className="flex-1 text-center relative px-4">
          <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 relative z-10 ${
            i === 0 ? 'bg-[#D4AF37] text-[#0A0A0A]' : 'bg-[#1C1C1C] border-2 border-[#D4AF37]/50 text-[#D4AF37]'
          }`}>
            <span className="text-xl font-bold">{phase.phase}</span>
          </div>
          <h4 className="text-lg font-semibold text-white mb-1">{phase.title}</h4>
          <p className="text-xs text-[#D4AF37] mb-2">{phase.duration}</p>
          <p className="text-sm text-zinc-400 mb-3">{phase.description}</p>
          <ul className="space-y-1">
            {phase.tasks.map((task) => (
              <li key={`task-${task}`} className="text-xs text-zinc-500">{task}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>

    {/* Mobile - Vertical */}
    <div className="md:hidden space-y-6">
      {PHASES.map((phase, i) => (
        <div key={phase.phase} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              i === 0 ? 'bg-[#D4AF37] text-[#0A0A0A]' : 'bg-[#1C1C1C] border-2 border-[#D4AF37]/50 text-[#D4AF37]'
            }`}>
              <span className="text-lg font-bold">{phase.phase}</span>
            </div>
            {i < PHASES.length - 1 && (
              <div className="w-px h-full bg-[#D4AF37]/30 mt-2" />
            )}
          </div>
          <div className="flex-1 pb-6">
            <h4 className="text-lg font-semibold text-white">{phase.title}</h4>
            <p className="text-xs text-[#D4AF37] mb-1">{phase.duration}</p>
            <p className="text-sm text-zinc-400 mb-2">{phase.description}</p>
            <ul className="space-y-1">
              {phase.tasks.map((task) => (
                <li key={`task-mobile-${task}`} className="text-xs text-zinc-500 flex items-center gap-2">
                  <Check className="w-3 h-3 text-[#D4AF37]" />
                  {task}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  </section>
);

// Key Takeaways Component
const KeyTakeaways = () => (
  <section id="takeaways" className="py-16 scroll-mt-20">
    <div className="bg-gradient-to-br from-[#D4AF37]/20 via-[#1C1C1C] to-[#1C1C1C] rounded-3xl border border-[#D4AF37]/30 p-8 sm:p-12">
      <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">
        Key Takeaways
      </h2>
      
      <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
        {TAKEAWAYS.map((takeaway) => (
          <div 
            key={`takeaway-${takeaway.slice(0, 25)}`}
            className="flex items-start gap-3 p-4 bg-[#0A0A0A]/50 rounded-xl border border-white/5"
          >
            <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 text-[#0A0A0A]" />
            </div>
            <p className="text-sm sm:text-base text-zinc-200">{takeaway}</p>
          </div>
        ))}
      </div>
      
      <div className="mt-8 text-center">
        <p className="text-zinc-400 text-sm max-w-xl mx-auto">
          The advantage won't come from better tools alone. It will come from how intentionally you train, refine, and deploy the systems built around them.
        </p>
      </div>
    </div>
  </section>
);

// Lead Capture / Download Section
const DownloadSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call - in production, connect to backend
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setSubmitted(true);
    setIsSubmitting(false);
  };

  return (
    <section id="download" className="py-16 scroll-mt-20">
      <div className="bg-[#1C1C1C] rounded-3xl border border-[#D4AF37]/20 overflow-hidden">
        <div className="grid lg:grid-cols-2">
          {/* Left - Info */}
          <div className="p-8 sm:p-12 bg-gradient-to-br from-[#D4AF37]/10 to-transparent">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D4AF37]/20 rounded-full mb-6">
              <Download className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs font-semibold text-[#D4AF37]">FREE DOWNLOAD</span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Download the Full Playbook
            </h2>
            
            <p className="text-zinc-400 mb-6">
              Get the complete LRS AI SDR Training Playbook as a PDF. Includes all 6 workflows, operator checklists, and implementation roadmap.
            </p>
            
            <ul className="space-y-3 text-sm text-zinc-300">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
                6 complete workflow breakdowns
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
                Operator checklists for each stage
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
                Failure modes and pro tips
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
                Implementation roadmap
              </li>
            </ul>
          </div>
          
          {/* Right - Form */}
          <div className="p-8 sm:p-12 flex items-center">
            {submitted ? (
              <div className="text-center w-full">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Check Your Email!</h3>
                <p className="text-zinc-400 text-sm">
                  The playbook is on its way. Check your inbox in the next few minutes.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="w-full space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Work Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                    placeholder="you@company.com"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-6 py-4 bg-[#D4AF37] text-[#0A0A0A] rounded-xl font-semibold hover:bg-[#E8C547] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Download className="w-5 h-5" />
                      Download PDF
                    </>
                  )}
                </button>
                
                <p className="text-xs text-zinc-500 text-center">
                  No spam. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Back to Top Button
const BackToTop = ({ show }) => (
  <button
    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    className={`fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-[#D4AF37] text-[#0A0A0A] shadow-lg shadow-[#D4AF37]/30 flex items-center justify-center transition-all duration-300 ${
      show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
    }`}
  >
    <ChevronUp className="w-6 h-6" />
  </button>
);

// Main Component
function AISDRPlaybookPage() {
  const [activeWorkflow, setActiveWorkflow] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
    // handleScroll is defined inside effect, setShowBackToTop is stable setState
  }, []);

  const scrollToWorkflow = (id) => {
    const element = document.getElementById(`workflow-${id}`);
    if (element) {
      const offset = 100;
      const y = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] overflow-x-hidden">
      <SEO 
        title="AI SDR Training Playbook | 6 Core Workflows"
        description="How to build AI SDR workflows that actually perform. 6 core workflows, operator checklists, and implementation roadmap for modern revenue teams."
        path="/resources/ai-sdr-playbook"
        image="resources"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 sm:pt-24 pb-8 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-purple-500/5" />
        </div>
        
        <div className="relative max-w-7xl mx-auto">
          {/* Featured Badge */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full">
              <Award className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-semibold text-[#D4AF37]">Featured System Playbook</span>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
            {/* Left - System Diagram (60%) */}
            <div className="lg:col-span-3 overflow-hidden">
              <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white tracking-tight leading-tight mb-4 break-words">
                The LRS AI SDR{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#E8C547] block sm:inline">
                  Training Playbook
                </span>
              </h1>
              
              <h2 className="text-sm sm:text-base lg:text-xl text-zinc-300 mb-4 sm:mb-6">
                How to Build AI SDR Workflows That Actually Perform
              </h2>
              
              <div className="bg-[#1C1C1C]/50 rounded-xl sm:rounded-2xl border border-white/5 p-4 sm:p-6 mb-4 sm:mb-6">
                <p className="text-zinc-400 mb-2 text-sm">
                  <span className="text-white font-medium">Most teams fail with AI SDRs</span> because they try to build one agent to do everything.
                </p>
                <p className="text-[#D4AF37] font-medium text-sm">
                  Top teams build systems of specialized workflows.
                </p>
              </div>
              
              {/* Interactive System Diagram */}
              <SystemDiagram 
                activeWorkflow={activeWorkflow}
                setActiveWorkflow={setActiveWorkflow}
                scrollToWorkflow={scrollToWorkflow}
              />
            </div>

            {/* Right - CTA Panel (40%) */}
            <div className="lg:col-span-2">
              <div className="bg-[#1C1C1C] rounded-2xl border border-white/10 p-6 lg:sticky lg:top-24">
                <h3 className="text-lg font-semibold text-white mb-4">What's Inside</h3>
                
                <p className="text-sm text-zinc-400 mb-6">
                  This playbook breaks down how high-performing teams design, train, and operate AI SDR systems across the entire outbound funnel.
                </p>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-3 text-sm text-zinc-300">
                    <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <Workflow className="w-3.5 h-3.5 text-[#D4AF37]" />
                    </div>
                    6 core AI SDR workflows
                  </li>
                  <li className="flex items-center gap-3 text-sm text-zinc-300">
                    <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#D4AF37]" />
                    </div>
                    Operator checklists per workflow
                  </li>
                  <li className="flex items-center gap-3 text-sm text-zinc-300">
                    <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <Play className="w-3.5 h-3.5 text-[#D4AF37]" />
                    </div>
                    Real execution model (not theory)
                  </li>
                </ul>
                
                <div className="space-y-3">
                  <button
                    onClick={() => scrollToWorkflow('prospecting')}
                    className="w-full px-6 py-3 bg-[#D4AF37] text-[#0A0A0A] rounded-xl font-semibold hover:bg-[#E8C547] transition-all flex items-center justify-center gap-2"
                  >
                    <BookOpen className="w-5 h-5" />
                    View Full Playbook
                  </button>
                  
                  <button
                    onClick={() => {
                      const el = document.getElementById('download');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="w-full px-6 py-3 bg-transparent border border-[#D4AF37]/50 text-[#D4AF37] rounded-xl font-semibold hover:bg-[#D4AF37]/10 transition-all flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Download PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full Playbook Content */}
      <div className="px-4 sm:px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Section Intro */}
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              The 6 Core AI SDR Workflows
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              An SDR is not one job. It's a stack of smaller jobs, each with different context requirements, quality bars, and failure modes. Here's how to train each one.
            </p>
          </div>

          {/* Workflow Sections */}
          <div className="space-y-16">
            {WORKFLOWS.map((workflow) => (
              <WorkflowSection key={workflow.id} workflow={workflow} />
            ))}
          </div>
        </div>
      </div>

      {/* Implementation Roadmap */}
      <div className="px-4 sm:px-6 bg-[#1C1C1C]/30">
        <div className="max-w-5xl mx-auto">
          <ImplementationRoadmap />
        </div>
      </div>

      {/* Key Takeaways */}
      <div className="px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <KeyTakeaways />
        </div>
      </div>

      {/* Download Section */}
      <div className="px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <DownloadSection />
        </div>
      </div>

      {/* Final CTA */}
      <div className="px-4 sm:px-6 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="p-8 sm:p-12 bg-gradient-to-br from-[#D4AF37]/20 to-[#1C1C1C] rounded-3xl border border-[#D4AF37]/30">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Ready to Build Your AI SDR System?
            </h3>
            <p className="text-zinc-300 mb-8 max-w-xl mx-auto">
              Let's discuss how these workflows can be implemented for your specific revenue challenges.
            </p>
            <a
              href="https://calendly.com/leitner-revenue-systems/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#0A0A0A] rounded-xl font-semibold hover:bg-[#E8C547] transition-all shadow-lg shadow-[#D4AF37]/20"
            >
              <Calendar className="w-5 h-5" />
              Book a Strategy Call
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <svg width="24" height="24" viewBox="0 0 64 64" fill="none">
              <rect x="6" y="46" width="11" height="14" fill={BRAND.gold} opacity="0.5" rx="2"/>
              <rect x="20" y="36" width="11" height="24" fill={BRAND.gold} opacity="0.65" rx="2"/>
              <rect x="34" y="24" width="11" height="36" fill={BRAND.gold} opacity="0.8" rx="2"/>
              <rect x="48" y="10" width="11" height="50" fill={BRAND.gold} rx="2"/>
            </svg>
            <span className="text-zinc-500 text-sm">© 2026 Leitner Revenue Systems</span>
          </div>
          <Link to="/resources" className="text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors">
            ← Back to Resources
          </Link>
        </div>
      </footer>

      <BackToTop show={showBackToTop} />
    </div>
  );
}

export default AISDRPlaybookPage;
